import type { Size } from "@volumental/common";
import type { SizeLocale } from "../api/gql/graphql";
import type {
  ScanQueryFragmentFragment,
  RecommendationQueryFragmentFragment,
  CompareToPopulationQueryFragmentFragment,
  StyleMetaDataQueryFragmentFragment,
} from "../api/gql/graphql";

type ScanNode = NonNullable<ScanQueryFragmentFragment["scan"]>;
export type Measurement = NonNullable<ScanNode["measurement"]>;
type GqlSizeRecommendation = NonNullable<RecommendationQueryFragmentFragment["sizeRecommendation"]>;
export type FitScore = NonNullable<GqlSizeRecommendation["fitScores"]>[number];
export type StyleMetaData = NonNullable<StyleMetaDataQueryFragmentFragment["styleMetaData"]>;
export type Percentile = NonNullable<CompareToPopulationQueryFragmentFragment["comparePreviousPurchaseToPopulation"]>;

export interface ScanListItem {
  type: "WebScan" | "RetailScan";
  id: string;
  createdAt: string;
  previousPurchaseID?: string;
}

export interface ScanResult {
  type: "WebScan" | "RetailScan";
  id: string;
  publicID: string;
  measurement: Measurement;
  meshes: {
    leftFoot: string;
    rightFoot: string;
  };
  ageGroup?: string;
  gender?: string;
  createdAt: string;
  claimed?: boolean;
}

export const toScanResult = (data: ScanNode | undefined | null): ScanResult | undefined => {
  if (!data?.measurement || !data.id || !data.publicID) {
    return undefined;
  }
  return {
    type: data.__typename,
    id: data.id,
    publicID: data.publicID,
    measurement: data.measurement,
    meshes: data.meshes ?? { leftFoot: "", rightFoot: "" },
    ageGroup: data.__typename === "RetailScan" ? (data.ageGroup ?? undefined) : undefined,
    gender: data.__typename === "RetailScan" ? (data.gender ?? undefined) : undefined,
    createdAt: data.createdAt as string,
    claimed: data.__typename === "WebScan" ? data.claimed : undefined,
  };
};

export interface SizeRecommendation {
  lengthSize: string;
  widthSize: string;
  predictorLevel: string;
  sizeLocale: SizeLocale;
  fitScores?: FitScore[];
}

export interface RecommendationError {
  code?: string;
  message: string;
}

export const toSizeRecommendation = (
  data: GqlSizeRecommendation | undefined | null
): SizeRecommendation | undefined => {
  if (data?.lengthSize && data.widthSize && data.sizeLocale && data.predictorLevel) {
    return {
      lengthSize: data.lengthSize,
      widthSize: data.widthSize,
      sizeLocale: data.sizeLocale,
      predictorLevel: data.predictorLevel,
      fitScores: data.fitScores ?? undefined,
    };
  }
  return undefined;
};

export enum CurrentView {
  HistorySelectScan = "history-select-scan",
  Intro = "intro",
  StepListIntro = "step-list-intro",

  Picker = "picker",
  SizeChart = "sizeChart",
  SizePicker = "size-picker",
  AddPPToScan = "add-pp-to-scan",
  ScannerIntro = "scanner-intro",
  ScannerPicker = "scanner-picker",
  Reconstructing = "reconstructing",
  ScannerDesktop = "scanner-desktop",
  Result = "result",
  RecomQRcodeView = "recom-qrcode-view",
}

export enum AppMode {
  Recommendation = "recommendation",
  Measurement = "measurement",
  Sizechart = "sizechart",
}

export enum CookieType {
  All = "all",
  Essential = "essential",
  Decline = "decline",
}

export enum UserFlowType {
  Solo = "solo",
  Assisted = "assisted",
  AssistedAdult = "assisted-adult",
  Hybrid = "hybrid",
  HistoryMScan = "history_mobile_scan",
  HistoryRScan = "history_retail_scan",
}

export const enum DisplayUnit {
  Centimeters = "cm",
  Inches = "“",
}

export interface FitSurveyResult {
  gender_age: string;
  brand_name: string;
  style_name?: string;
  width?: number;
  length: number;
  size_locale: SizeLocale;
  widthLabel?: string;
  lengthLabel: string;
  brandLabel: string;
}

export interface GenderAgeChart {
  genderAge: string;
  sizes: Size[];
}

export interface SizeChartData {
  predictorLevel?: string;
  allCharts: GenderAgeChart[];
  shoe: StyleMetaData;
}
